// src/DetailingList.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSession } from './auth/SessionProvider.jsx';
import { useNavigate } from 'react-router-dom';
import { api } from './lib/apiFetch';
import DetailingForm from './DetailingForm.jsx';

// Safe lowercase helper (prevents "toLowerCase of undefined")
const lc = (v) => String(v ?? '').toLowerCase();

// Normalize ANY backend shape to an array we can .filter/.map safely
const toList = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.data))  return data.data;
  return [];
};

// API paths (relative; api helper adds REACT_APP_API_BASE + Authorization)
const INACTIVE_PATH = '/alltrucks?type=200';
const ACTIVE_PATH   = '/alltrucks?type=900';
// If you deploy the new cleaning Lambda that supports flat arrays, you can swap:
// const ACTIVE_PATH = '/cleaning?view=flat&section=currentWeek';

export default function DetailingList() {
  const navigate = useNavigate();
  const { signedIn } = useSession();
  const isAuthed = signedIn;

  const [items, setItems] = useState([]);
  const [selectedCleaningItem, setSelectedCleaningItem] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'unitNumber', direction: 'ascending' });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  // ---- Data fetch (active type=900 minus inactives from type=200) ----
  const fetchItems = useCallback(async () => {
    // Require auth for this view
    if (!isAuthed) {
      setError('Please sign in to view the detailing list.');
      setItems([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Fetch both lists in parallel (inactive=type 200, active=type 900)
      const [inactiveResp, activeResp] = await Promise.all([
        api.get(INACTIVE_PATH).catch((err) => ({ __error: err })), // capture; normalize below
        api.get(ACTIVE_PATH).catch((err) => ({ __error: err })),
      ]);

      // Normalize to arrays so .filter/.map are always safe
      const inactiveData = toList(inactiveResp);
      const activeData   = toList(activeResp);

      // Build a set of inactive unit numbers (explicitly status === 'I')
      const inactiveUnits = new Set(
        inactiveData
          .filter((x) => (x?.unitStatus ?? '') === 'I')
          .map((x) => x?.unitNumber)
          .filter(Boolean)
      );

      // Keep only active entries whose unitNumber is NOT marked inactive
      const filteredActive = activeData.filter((it) => !inactiveUnits.has(it?.unitNumber));

      setItems(filteredActive);
    } catch (err) {
      // Final safety net — surface message and clear list
      setError(String(err?.message || err));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  useEffect(() => { void fetchItems(); }, [fetchItems]);

  // ---- Sorting ----
  const requestSort = (key) => {
    setSortConfig((prev) => {
      const direction = prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending';
      return { key, direction };
    });
  };

  // ---- Row click handlers ----
  const handleRowClick   = (item) => setSelectedCleaningItem(item);
  const handleCancelEdit = () => setSelectedCleaningItem(null);

  const handleAddCleaningItem = () => {
    const newItem = {
      id: new Date().toISOString(),
      type: 900,
      unitNumber: '',
      manufacturer: '',
      style: '',
      assignment: '',
    };
    setSelectedCleaningItem(newItem);
  };

  // ---- Derived, safe sorted + filtered items ----
  const sortedItems = useMemo(() => {
    const list = Array.isArray(items) ? [...items] : [];

    // sort
    const { key, direction } = sortConfig;
    if (key) {
      list.sort((a, b) => {
        const av = String(a?.[key] ?? '');
        const bv = String(b?.[key] ?? '');
        if (av < bv) return direction === 'ascending' ? -1 : 1;
        if (av > bv) return direction === 'ascending' ?  1 : -1;
        return 0;
      });
    }

    // search
    const q = lc(searchTerm);
    if (!q) return list;

    return list.filter((item) =>
      [
        item?.unitNumber,
        item?.style,
        item?.lastCleaned,
        item?.availStart,
        item?.availEnd,
        item?.nextStart,
      ]
        .filter((v) => v !== undefined && v !== null)
        .some((v) => lc(v).includes(q))
    );
  }, [items, sortConfig, searchTerm]);

  return (
    <div className="container listDiv truckContent">
      {!isAuthed && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ color: 'crimson', marginBottom: 6 }}>
            You must sign in to view and edit the detailing list.
          </div>
          <button className="link-btn" onClick={() => navigate('/login')}>
            Go to Sign In
          </button>
        </div>
      )}

      <div className="search-and-add-bar">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={!isAuthed}
        />
        <button onClick={handleAddCleaningItem} disabled={!isAuthed}>Add Cleaning</button>
        <button onClick={fetchItems} disabled={loading || !isAuthed}>Refresh</button>
      </div>

      {error && <div style={{ color: 'crimson', margin: '8px 0' }}>{error}</div>}

      <div className="table-container">
        {loading ? (
          <div>Loading…</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="styleCol1" onClick={() => requestSort('unitNumber')}>Unit Number</th>
                <th className="styleCol2" onClick={() => requestSort('style')}>Style</th>
                <th className="styleCol1" onClick={() => requestSort('lastCleaned')}>Last Cleaned</th>
                <th className="styleCol1" onClick={() => requestSort('availStart')}>Availability</th>
                <th className="styleCol1" onClick={() => requestSort('nextStart')}>Next Cleaning</th>
              </tr>
            </thead>
            <tbody className="scrollable-tbody">
              {sortedItems.map((item, idx) => (
                <tr
                  key={item?.id || `${item?.unitNumber || 'row'}-${idx}`}
                  onClick={() => handleRowClick(item)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="styleCol1">{item?.unitNumber || '—'}</td>
                  <td className="styleCol1">{item?.style || '—'}</td>
                  <td className="styleCol1">{item?.lastCleaned || '—'}</td>
                  <td className="styleCol2">
                    {item?.availStart || '—'}
                    {(item?.availStart || item?.availEnd) ? ' to ' : ''}
                    {item?.availEnd || '—'}
                  </td>
                  <td className="styleCol1">{item?.nextStart || '—'}</td>
                </tr>
              ))}
              {!sortedItems.length && (
                <tr><td colSpan={5} style={{ color: '#666' }}>No matches</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {selectedCleaningItem && (
        <div className="container formDiv">
          <DetailingForm
            item={selectedCleaningItem}
            onCancelEdit={handleCancelEdit}
            onSuccessfulSubmit={fetchItems}
          />
        </div>
      )}
    </div>
  );
}
